import { useMemo, useState } from 'react';
import { Box, Button, Divider, Flex, Text } from '@chakra-ui/react';
import {
  EyeOutline as Eye,
  RecognitionEmployeeLeaderboardEntry,
} from '@aslanpay/portal-common-lib';

import LeaderboardTabs, { LeaderboardMetric } from '../LeaderboardTabs';
import LeaderboardRow from '../LeaderboardRow';

interface EmployeeLeaderboardProps {
  title: string;
  subtitle: string;
  entries: RecognitionEmployeeLeaderboardEntry[];
  hasMore: boolean;
  onSeeAll?: (metric: LeaderboardMetric) => void;
}

function EmptyState() {
  return (
    <Flex flexDir="column" align="center" justify="center" py="40px" gap="8px">
      <Text textStyle="h5Medium" color="brand.darkPurple">
        Things are just getting started
      </Text>
      <Text textStyle="small" color="brand.darkGray" textAlign="center">
        Recognition insights will appear here once people begin sending recognitions.
      </Text>
    </Flex>
  );
}

function EmployeeLeaderboard({
  title,
  subtitle,
  entries,
  hasMore,
  onSeeAll = undefined,
}: EmployeeLeaderboardProps) {
  const [metric, setMetric] = useState<LeaderboardMetric>('count');
  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => b[metric] - a[metric]),
    [entries, metric]
  );
  return (
    <Flex
      bg="brand.lightGray2"
      borderRadius="10px"
      border="1px solid"
      borderColor="brand.lightGray1"
      w="full"
      p="20px 30px"
      flexDir="column"
      gap="20px"
    >
      <Flex justify="space-between" align="flex-start">
        <Flex flexDir="column" gap="4px">
          <Text fontSize="24px" fontWeight="600" lineHeight="1.4" color="brand.mutedPurple">
            {title}
          </Text>
          <Text textStyle="smallMedium" color="brand.darkGray">
            {subtitle}
          </Text>
        </Flex>
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            color="brand.purple"
            leftIcon={<Eye boxSize="16px" />}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            pr={0}
            onClick={() => onSeeAll?.(metric)}
          >
            See all
          </Button>
        )}
      </Flex>

      <LeaderboardTabs value={metric} onChange={setMetric} />

      {entries.length === 0 ? (
        <EmptyState />
      ) : (
        <Flex flexDir="column">
          {sortedEntries.map((entry, index) => (
            <Box key={entry.individualId}>
              {index > 0 && <Divider borderColor="brand.lightGray1" />}
              <LeaderboardRow
                rank={index + 1}
                primary={`${entry.firstName} ${entry.lastName}`}
                secondary={entry.jobTitle || undefined}
                value={metric === 'count' ? entry.count : entry.points}
              />
            </Box>
          ))}
        </Flex>
      )}
    </Flex>
  );
}

export default EmployeeLeaderboard;
